import React, { useState, useEffect } from 'react';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import Container from '@mui/material/Container';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import SearchIcon from '@mui/icons-material/Search';
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';
import Modal from '@mui/material/Modal';
import Tooltip from '@mui/material/Tooltip';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import CircularProgress from "@mui/material/CircularProgress"; // Import loading spinner
import TablePagination from '@mui/material/TablePagination'; // Import TablePagination
import { useNavigate, useParams } from 'react-router-dom';
import base64 from 'base64-js';
import Snackbar from '@mui/material/Snackbar';
import MuiAlert from '@mui/material/Alert';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import LocationOnIcon from "@mui/icons-material/LocationOn";
import {
  AppBar, Toolbar, FormControl, InputLabel, Select, MenuItem,
  Dialog, DialogTitle, DialogContent, DialogActions,
} from '@mui/material';

const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

// ── User Module Guide Steps ────────────────────────────────────────────────
const userGuideData = [
  {
    section: "🔍 Search & Filter",
    steps: [
      {
        label: "Search by Employee Name",
        desc: "Type an employee name in the search box to instantly filter the user table. Search is case-insensitive and resets pagination to page 1 automatically.",
      },
      {
        label: "Branch Dropdown (Master Users Only)",
        desc: "Visible only for Master/Admin users. Select a branch from the AppBar dropdown to load users belonging to that specific branch. Non-master users automatically see their own branch data.",
      },
    ],
  },
  {
    section: "➕ Adding Users",
    steps: [
      {
        label: "Add User Button",
        desc: "Click 'Add User' to navigate to the Add User form where you can register a new system user by linking them to an employee and assigning a role, username, and password.",
      },
    ],
  },
  {
    section: "📋 User Table Columns",
    steps: [
      {
        label: "User Role Name",
        desc: "The role/group assigned to this user (e.g. Admin, Teacher, Accountant). Determines what modules and actions this user can access in the system.",
      },
      {
        label: "Employee Name",
        desc: "The name of the employee this user account is linked to. Used to identify which staff member owns this login.",
      },
      {
        label: "User Name",
        desc: "The login username for this user. Used along with the password to authenticate into the system.",
      },
      {
        label: "Password",
        desc: "Displayed as asterisks (****) by default for security. Click the 👁️ eye icon on the right of the password field to toggle visibility and reveal the actual password.",
      },
      {
        label: "Password Toggle (Eye Icon)",
        desc: "Each row has an independent eye icon. Clicking it shows or hides only that row's password. Other rows are unaffected. Click again to hide.",
      },
      {
        label: "Expiry Date",
        desc: "The date on which this user account will expire and lose access to the system. Ensure accounts are renewed before expiry to avoid login issues.",
      },
    ],
  },
  {
    section: "✏️ Edit & 🗑️ Delete Actions",
    steps: [
      {
        label: "Edit Button",
        desc: "Click the orange 'EDIT' button to navigate to the Edit User form with the selected user's current data pre-filled. The branch ID is passed in the URL for accurate data loading.",
      },
      {
        label: "Authorization — Edit",
        desc: "The Edit button is disabled if your account does not have Modify permission. Hover to see 'You are not authorized to edit'. Contact your administrator to grant access.",
      },
      {
        label: "Delete Button",
        desc: "Click the red 'DELETE' button to open a confirmation modal before permanently removing the user account from the system.",
      },
      {
        label: "Delete Confirmation Modal",
        desc: "A popup shows the user's role name and asks 'Are you sure?'. Click 'Yes, Delete' to confirm permanent deletion, or 'Cancel' to safely close without deleting.",
      },
      {
        label: "Authorization — Delete",
        desc: "The Delete button is disabled if your account does not have Delete permission. Hover to see 'You are not authorized to delete'. Contact your administrator.",
      },
    ],
  },
  {
    section: "📄 Pagination",
    steps: [
      {
        label: "Page Navigation",
        desc: "Users are paginated at 10 records per page. Use the pagination controls at the bottom of the table to navigate between pages. Pagination resets to page 1 when you search.",
      },
      {
        label: "Total Count",
        desc: "The pagination bar shows the total number of filtered users and the current range being displayed (e.g. '1–10 of 45').",
      },
    ],
  },
  {
    section: "🔔 Notifications",
    steps: [
      {
        label: "Success Snackbar (Green)",
        desc: "Shown at the top-center when a user is deleted successfully.",
      },
      {
        label: "Error Snackbar (Red)",
        desc: "Shown when fetching user data fails, delete fails, or an unexpected API error occurs.",
      },
      {
        label: "Warning Snackbar (Orange)",
        desc: "Shown for authorization-related issues such as an invalid country name response from the Module Authorizer API.",
      },
    ],
  },
];

// ── User Guide Modal ───────────────────────────────────────────────────────
const UserGuideModal = ({ open, onClose }) => (
  <Dialog
    open={open}
    onClose={onClose}
    maxWidth="sm"
    fullWidth
    PaperProps={{
      sx: {
        borderRadius: "14px",
        overflow: "hidden",
        boxShadow: "0 8px 32px rgba(0,0,0,0.25)",
      },
    }}
  >
    {/* ── Gradient header matching AppBar theme ── */}
    <DialogTitle
      sx={{
        background: "linear-gradient(135deg, #0B1F3D 0%, #1565c0 60%, #26c6da 100%)",
        color: "#fff",
        fontWeight: 700,
        fontSize: "1.1rem",
        textAlign: "center",
        py: 2.5,
        letterSpacing: "0.04em",
      }}
    >
      👤 User Management — User Guide
    </DialogTitle>

    {/* ── Scrollable content ── */}
    <DialogContent
      dividers
      sx={{
        px: 3,
        py: 2.5,
        maxHeight: "62vh",
        overflowY: "auto",
        backgroundColor: "#fafafa",
      }}
    >
      {userGuideData.map((group, gi) => (
        <Box key={gi} sx={{ mb: gi < userGuideData.length - 1 ? 3 : 0 }}>

          {/* Section heading */}
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: "0.95rem",
              color: "#1565c0",
              mb: 1.5,
              pb: 0.5,
              borderBottom: "2px solid #bbdefb",
            }}
          >
            {group.section}
          </Typography>

          {/* Step list */}
          {group.steps.map((step, si) => (
            <Box
              key={si}
              sx={{ display: "flex", alignItems: "flex-start", gap: 1.5, mb: 1.2 }}
            >
              {/* Bullet dot */}
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: "50%",
                  backgroundColor: "#1976d2",
                  mt: "5px",
                  flexShrink: 0,
                }}
              />
              <Typography sx={{ fontSize: "0.875rem", color: "#333", lineHeight: 1.6 }}>
                <Box component="span" sx={{ fontWeight: 700, color: "#111" }}>
                  {step.label}
                </Box>
                {" — "}
                {step.desc}
              </Typography>
            </Box>
          ))}
        </Box>
      ))}

      {/* ── Bottom tip box ── */}
      <Box
        sx={{
          mt: 2.5,
          p: 1.5,
          borderRadius: "8px",
          backgroundColor: "#e3f2fd",
          border: "1px solid #90caf9",
        }}
      >
        <Typography sx={{ fontSize: "0.8rem", color: "#1565c0", fontStyle: "italic" }}>
          💡 <strong>Tip:</strong> Use the 👁️ <strong>eye icon</strong> in the Password column to safely verify a user's password without exposing all passwords at once. Master users should select the correct <strong>Branch</strong> before searching for accurate results.
        </Typography>
      </Box>
    </DialogContent>

    {/* ── Close button ── */}
    <DialogActions sx={{ justifyContent: "center", py: 2, backgroundColor: "#fafafa" }}>
      <Button
        onClick={onClose}
        variant="contained"
        sx={{
          background: "linear-gradient(45deg, #0B1F3D 30%, #1976d2 90%)",
          borderRadius: "25px",
          px: 5,
          py: 1,
          fontWeight: 600,
          textTransform: "none",
          fontSize: "0.9rem",
          boxShadow: "0 3px 8px rgba(11,31,61,0.35)",
          "&:hover": {
            background: "linear-gradient(45deg, #0a1a33 30%, #1565c0 90%)",
          },
        }}
      >
        Close
      </Button>
    </DialogActions>
  </Dialog>
);

const User = () => {
  const [isMaster, setIsMaster] = useState(false);
  const [branches, setBranches] = useState([]);
  const [selectedBranchId, setSelectedBranchId] = useState(1);

  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [users, setUsers] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(false); // Loading state
  const [selectedUser, setSelectedUser] = useState({});
  const [showPasswords, setShowPasswords] = useState({});

  // Controls the User Guide modal open/close
  const [guideOpen, setGuideOpen] = useState(false);

  useEffect(() => {
    const isMasterUser = sessionStorage.getItem("is_Master") === "true";
    setIsMaster(isMasterUser);

    if (isMasterUser) {
      fetchBranches();
    } else {
      const branchId = sessionStorage.getItem("branchId");
      setSelectedBranchId(branchId);
    }
  }, []);

  useEffect(() => {
    if (selectedBranchId) {
      fetchData();
    }
  }, [selectedBranchId]);

  const handleTogglePassword = (userId) => {
    setShowPasswords(prev => ({
      ...prev,
      [userId]: !prev[userId]
    }));
  };

  const navigate = useNavigate();

  // Pagination states
  const [page, setPage] = useState(0);
  const [rowsPerPage] = useState(10); // Fixed to 30 items per page

  const [authorization, setAuthorization] = useState([]);
  const [canDelete, setCanDelete] = useState(true); // Default to true, assuming user can delete
  const [canEdit, setCanEdit] = useState(true); // Default to true, assuming user can edit

  const { encodedFormId } = useParams();

  const decodeFormId = (encodedFormId) => {
    const bytes = base64.toByteArray(encodedFormId);
    return new TextDecoder().decode(bytes);
  };
  const formId = decodeFormId(encodedFormId);
  console.log(formId);

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
    setPage(0); // Reset to first page on search
  };

  const handleAddUser = () => {
    navigate('/adduser');
  };

  const handleEditUser = (userId) => {
    navigate(`/edituser/${userId}?Branch_Id=${selectedBranchId}`);
  };

  const handleShowModal = (user, userId) => {
    setSelectedUser(user);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
  };

  const handleDeleteUser = async (userId) => {
    try {
      const apiUrl = process.env.REACT_APP_BASE_URL;
      const token = sessionStorage.getItem('token');
      const response = await fetch(
        `${apiUrl}/CPanel/UserId?UserId=${selectedUser.userId}`,
        {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
            Authorization: token,
          },
        }
      );

      if (response.ok) {
        // Assuming successful deletion, you can refresh the data
        fetchData();
        setShowModal(false);
        setSnackbarMessage('User deleted successfully!');
        setOpenSnackbar(true);
        setSnackbarSeverity("success");
      } else {
        console.error('Delete failed');
        // alert('Failed to delete user');
        setSnackbarMessage('Failed to delete user');
        setOpenSnackbar(true);
        setSnackbarSeverity("error");
      }
    } catch (error) {
      console.error('API request error:', error);
      // alert('An error occurred. Please try again later.');
      setSnackbarMessage('An error occurred. Please try again later.');
      setOpenSnackbar(true);
      setSnackbarSeverity("error");
    }
  };

  const fetchBranches = async () => {
    try {
      const apiUrl = process.env.REACT_APP_BASE_URL;
      const token = sessionStorage.getItem("token");
      const response = await fetch(`${apiUrl}/Branch/GetBranch`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: token,
        },
        body: JSON.stringify({}),
      });

      if (response.ok) {
        const data = await response.json();
        setBranches(data);
      } else {
        console.error("Failed to fetch branches");
      }
    } catch (error) {
      console.error("Error fetching branches:", error);
    }
  };

  const fetchData = async () => {
    try {
      setLoading(true); // Start loading
      const apiUrl = process.env.REACT_APP_BASE_URL;
      const token = sessionStorage.getItem("token");
      const branchId = isMaster ? selectedBranchId : sessionStorage.getItem("branchId");
      const response = await fetch(`${apiUrl}/CPanel/GetUser?Branch_Id=${branchId}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: token,
        },
        body: JSON.stringify({}),
      });

      if (response.ok) {
        const responseData = await response.json();
        if (Array.isArray(responseData) && responseData.length > 0) {
          setUsers(responseData);
        } else {
          setUsers([]); // Ensure users is an empty array if no data is found
        }
      } else {
        console.error("Failed to fetch user data");
        // alert("Failed to fetch user data");
        setSnackbarMessage('Failed to fetch user data');
        setOpenSnackbar(true);
        setSnackbarSeverity("error");
        setUsers([]); // Set empty users on error
      }
    } catch (error) {
      console.error("API request error:", error);
      // alert("An error occurred. Please try again later.");
      setSnackbarMessage('An error occurred. Please try again later.');
      setOpenSnackbar(true);
      setSnackbarSeverity("error");
      setUsers([]); // Set empty users on error
    } finally {
      setLoading(false); // Stop loading
    }
  };

  const Authorizer = async () => {
    try {
      const apiUrl = process.env.REACT_APP_BASE_URL;
      //setLoadingBarProgress(30);
      const token = sessionStorage.getItem('token');
      const response = await fetch(`${apiUrl}/CPanel/Module_Authorizer`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: token,
        },
        body: JSON.stringify({
          formId,
        }),
      });

      if (response.ok) {
        const responseData = await response.json();
        setAuthorization(responseData);
        // Check permissions for Delete and Edit
        const authorizationData = responseData[0];
        setCanDelete(authorizationData.uDelete === 1);
        setCanEdit(authorizationData.uModify === 1);
        // setLoadingBarProgress(100);
      } else {
        console.error('Country name incorrect');
        // alert('Invalid country name');
        setSnackbarMessage('Invalid country name');
        setOpenSnackbar(true);
        setSnackbarSeverity("warning");
        // setLoadingBarProgress(0);
      }
    } catch (error) {
      console.error('API request error:', error);
      // alert('An error occurred. Please try again later.');
      setSnackbarMessage('An error occurred. Please try again later.');
      setOpenSnackbar(true);
      setSnackbarSeverity("error");
    }
  };

  useEffect(() => {
    fetchData();
    Authorizer();
  }, []);

  // Filter users based on search term
  const filteredUsers = users.filter(user =>
    user.employeeName.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Paginate the filtered users
  const paginatedUsers = filteredUsers.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  // Handle page change
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  return (
    <Container>

      {/* ── User Guide Modal ── */}
      <UserGuideModal open={guideOpen} onClose={() => setGuideOpen(false)} />

      <AppBar
        position="static"
        sx={{
          backgroundColor: "#0B1F3D",
          boxShadow: "0 2px 8px rgba(0,0,0,0.3)",
          mb: 1,
        }}
      >
        <Toolbar sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", px: 3 }}>

          {/* Left: Title + ℹ️ Button grouped together */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 700,
                letterSpacing: "0.5px",
                color: "#ffffff",
              }}
            >
              User
            </Typography>

            {/* ── ℹ️ User Guide Button — sits right next to "User" title ── */}
            <Tooltip title="View User Guide" placement="bottom" arrow>
              <IconButton
                onClick={() => setGuideOpen(true)}
                size="small"
                sx={{
                  width: 34,
                  height: 34,
                  borderRadius: "50%",
                  backgroundColor: "rgba(255,255,255,0.15)",
                  border: "2px solid rgba(255,255,255,0.7)",
                  color: "#fff",
                  fontWeight: 900,
                  fontSize: "1rem",
                  fontFamily: "serif",
                  transition: "all 0.2s ease",
                  "&:hover": {
                    backgroundColor: "rgba(255,255,255,0.3)",
                    border: "2px solid #fff",
                    transform: "scale(1.12)",
                    boxShadow: "0 0 12px rgba(255,255,255,0.4)",
                  },
                }}
              >
                i
              </IconButton>
            </Tooltip>
          </Box>

          {/* Right: Branch Dropdown — Master Users Only */}
          {isMaster && (
            <Box sx={{ position: "relative" }}>
              <FormControl size="small" sx={{ width: 220 }}>
                <InputLabel
                  sx={{
                    color: "rgba(255,255,255,0.6)",
                    fontSize: "0.75rem",
                    fontWeight: 500,
                    letterSpacing: "0.6px",
                    textTransform: "uppercase",
                    top: "-2px",
                    "&.Mui-focused": { color: "#64B5F6" },
                    "&.MuiFormLabel-filled": { color: "rgba(255,255,255,0.7)" },
                  }}
                >
                  Branch
                </InputLabel>
                <Select
                  value={selectedBranchId || ""}
                  onChange={(e) => setSelectedBranchId(e.target.value)}
                  label="Branch"
                  startAdornment={
                    <InputAdornment position="start">
                      <LocationOnIcon sx={{ fontSize: 16, color: "rgba(255,255,255,0.5)", ml: 0.5 }} />
                    </InputAdornment>
                  }
                  sx={{
                    color: "#ffffff",
                    fontWeight: 500,
                    fontSize: "0.875rem",
                    letterSpacing: "0.3px",
                    backgroundColor: "rgba(255,255,255,0.07)",
                    backdropFilter: "blur(8px)",
                    borderRadius: "8px",
                    transition: "background-color 0.2s ease, box-shadow 0.2s ease",
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: "rgba(255,255,255,0.15)",
                      borderWidth: "1px",
                      transition: "border-color 0.2s ease",
                    },
                    "&:hover": {
                      backgroundColor: "rgba(255,255,255,0.11)",
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: "rgba(255,255,255,0.35)",
                      },
                    },
                    "&.Mui-focused": {
                      backgroundColor: "rgba(255,255,255,0.1)",
                      boxShadow: "0 0 0 3px rgba(100,181,246,0.18)",
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#64B5F6",
                        borderWidth: "1.5px",
                      },
                    },
                    "& .MuiSelect-icon": {
                      color: "rgba(255,255,255,0.5)",
                      transition: "color 0.2s ease",
                    },
                    "&.Mui-focused .MuiSelect-icon": {
                      color: "#64B5F6",
                    },
                    "& .MuiSelect-select": {
                      py: 1.1,
                    },
                  }}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        mt: 0.5,
                        borderRadius: "10px",
                        border: "1px solid rgba(11,31,61,0.12)",
                        boxShadow: "0 8px 32px rgba(11,31,61,0.18), 0 2px 8px rgba(0,0,0,0.08)",
                        overflow: "hidden",
                        "& .MuiList-root": { py: 0.5 },
                      },
                    },
                  }}
                >
                  {branches.map((branch) => (
                    <MenuItem
                      key={branch.branchId}
                      value={branch.branchId}
                      sx={{
                        fontSize: "0.875rem",
                        fontWeight: 500,
                        color: "#0B1F3D",
                        px: 2,
                        py: 1.1,
                        mx: 0.5,
                        borderRadius: "6px",
                        transition: "background-color 0.15s ease",
                        "&:hover": {
                          backgroundColor: "rgba(11,31,61,0.07)",
                        },
                        "&.Mui-selected": {
                          backgroundColor: "rgba(25,118,210,0.1)",
                          color: "#1565C0",
                          fontWeight: 600,
                          "&:hover": {
                            backgroundColor: "rgba(25,118,210,0.15)",
                          },
                        },
                      }}
                    >
                      {branch.branchName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          )}

        </Toolbar>
      </AppBar>

      <Paper elevation={3} style={{ padding: '20px', marginTop: 6 }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "10px",
            marginBottom: "20px",
            justifyContent: 'space-between',
          }}
        >
          <TextField
            label="Search by Employee Name"
            variant="outlined"
            value={searchTerm}
            onChange={handleSearchChange}
            style={{ width: "300px" }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton>
                    <SearchIcon />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <Button
            variant="contained"
            color="primary"
            onClick={handleAddUser}
            style={{
              width: "100px",
              height: "50px",
              padding: "10px 0",
              fontWeight: 'bold',
            }}
          >
            Add User
          </Button>
        </div>

        {loading ? (
          <div style={{ display: "flex", justifyContent: "center", marginTop: "20px" }}>
            <CircularProgress />
          </div>
        ) : (
          <>
            <TableContainer component={Paper} style={{ marginTop: 16 }}>
              <Table
                sx={{
                  minWidth: 650,
                  border: '1px solid #ccc',
                  '& .MuiTableCell-root': {
                    borderRight: '1px solid #ccc', // vertical line
                    fontSize: '1rem', // increased font size
                  },
                  '& .MuiTableHead-root .MuiTableCell-root': {
                    fontWeight: 'bold',
                    backgroundColor: '#f5f5f5',
                  },
                }}
              >
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ borderRight: '1px solid #ccc', borderBottom: '1px solid #ccc', fontWeight: 'bold', fontSize: '1.1rem' }}>
                      User Role Name
                    </TableCell>
                    <TableCell sx={{ borderRight: '1px solid #ccc', borderBottom: '1px solid #ccc', fontWeight: 'bold', fontSize: '1.1rem' }}>
                      Employee Name
                    </TableCell>
                    <TableCell sx={{ borderRight: '1px solid #ccc', borderBottom: '1px solid #ccc', fontWeight: 'bold', fontSize: '1.1rem' }}>
                      User Name
                    </TableCell>
                    <TableCell sx={{ borderRight: '1px solid #ccc', borderBottom: '1px solid #ccc', fontWeight: 'bold', fontSize: '1.1rem' }}>
                      Password
                    </TableCell>
                    <TableCell sx={{ borderRight: '1px solid #ccc', borderBottom: '1px solid #ccc', fontWeight: 'bold', fontSize: '1.1rem' }}>
                      Expiry Date
                    </TableCell>
                    <TableCell sx={{ borderBottom: '1px solid #ccc', textAlign: 'center', fontWeight: 'bold', fontSize: '1.1rem' }}>
                      Action
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedUsers.length > 0 ? (
                    paginatedUsers.map((user) => (
                      <TableRow key={user.userId} sx={{ borderTop: '1px solid #ccc' }}>
                        <TableCell sx={{ borderRight: '1px solid #ccc', fontSize: '1rem' }}>
                          {user.userGroupName}
                        </TableCell>
                        <TableCell sx={{ borderRight: '1px solid #ccc', fontSize: '1rem' }}>
                          {user.employeeName}
                        </TableCell>
                        <TableCell sx={{ borderRight: '1px solid #ccc', fontSize: '1rem' }}>
                          {user.userName}
                        </TableCell>
                        <TableCell sx={{ borderRight: '1px solid #ccc', fontSize: '1rem' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0 }}>
                            <Typography
                              sx={{
                                flexGrow: 1,
                                fontSize: '1rem',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}
                              title={showPasswords[user.userId] ? user.password : '*'.repeat(user.password?.length || 0)}
                            >
                              {showPasswords[user.userId] ? user.password : '*'.repeat(user.password?.length || 0)}
                            </Typography>
                            <IconButton
                              onClick={() => handleTogglePassword(user.userId)}
                              size="small"
                              edge="end"
                              aria-label="toggle password visibility"
                            >
                              {showPasswords[user.userId] ? <VisibilityOff /> : <Visibility />}
                            </IconButton>
                          </Box>
                        </TableCell>
                        <TableCell sx={{ borderRight: '1px solid #ccc', fontSize: '1rem' }}>
                          {user.expiryDate}
                        </TableCell>
                        <TableCell sx={{ textAlign: 'center', fontSize: '1rem' }}>
                          <Tooltip title={canEdit ? "" : "You are not authorized to edit"} arrow>
                            <span>
                              <Button
                                onClick={() => handleEditUser(user.userId)}
                                variant="contained"
                                color="warning"
                                disabled={!canEdit}
                                sx={{ marginRight: 1 }}
                                startIcon={<EditIcon />}
                              >
                                EDIT
                              </Button>
                            </span>
                          </Tooltip>
                          <Tooltip title={canDelete ? "" : "You are not authorized to delete"} arrow>
                            <span>
                              <Button
                                className="mx-2"
                                onClick={() => handleShowModal(user)}
                                variant="contained"
                                color="error"
                                disabled={!canDelete}
                                sx={{ marginRight: 1 }}
                                startIcon={<DeleteIcon />}
                              >
                                DELETE
                              </Button>
                            </span>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={6} align="center">
                        No users found
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              rowsPerPageOptions={[30]} // Fixed to 30
              component="div"
              count={filteredUsers.length} // Total number of filtered users
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
            />
          </>
        )}
      </Paper>

      {/* Delete Confirmation Modal */}
      <Modal open={showModal} onClose={handleCloseModal} aria-labelledby="delete-modal-title" aria-describedby="delete-modal-description">
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 400,
            bgcolor: 'background.paper',
            borderRadius: 2,
            boxShadow: 24,
            overflow: 'hidden',
          }}
        >
          {/* Header */}
          <Box sx={{ backgroundColor: '#0B1F40', padding: '12px 16px' }}>
            <Typography id="delete-modal-title" variant="h6" sx={{ color: '#fff' }}>
              Delete Confirmation ⚠️
            </Typography>
          </Box>

          {/* Content */}
          <Box sx={{ padding: '20px' }}>
            <Typography id="delete-modal-description" sx={{ mb: 2 }}>
              Are you sure you want to delete the user with role name{" "}
              <strong>{selectedUser.userGroupName}</strong>?
            </Typography>

            {/* Buttons */}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
              <Button onClick={handleCloseModal} variant="outlined" sx={{ mr: 1 }}>
                Cancel
              </Button>
              <Button
                variant="contained"
                color="error"
                onClick={() => handleDeleteUser(selectedUser.id)}
              >
                Yes, Delete
              </Button>
            </Box>
          </Box>
        </Box>
      </Modal>

      <Snackbar
        open={openSnackbar}
        autoHideDuration={4000}
        onClose={() => setOpenSnackbar(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setOpenSnackbar(false)} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>

    </Container>
  );
};

export default User;